import TeX2Max from 'tex2max';
import { t } from './i18nService';
import { convertAsciiMathToLatex } from './ParseMathStringService';
import marketplacePropertiesService from './MarketplacePropertiesService';

const createRegExp = (str, opts) => (new RegExp(str.raw[0].replace(/\s/gm, ''), opts || ''));

const RESTRICTED_VALUES_ARRAY = ['<', '>', '='];

const MATH_INPUT_FIELD_WIDTH_MULTIPLIER = 8;
const MAX_CONTENT_WIDTH = 700;
const ICON_WIDTH = 44;

const reactMathKeyboardService = {

    getInvalidValueFromInput(value) {
        let invalidValue = value;
        let match;
        const regex = reactMathKeyboardService.validationPattern;

        // Use a do-while loop to handle the first iteration
        do {
            match = regex.exec(value);
            if (match !== null) {
                invalidValue = invalidValue.replace(match[0], '');
            }
        } while (match !== null);

        return invalidValue;
    },

    // pattern to cover all elements from virtual keyboard:
    // numbers, letters, math operations, greek letters
    validationPattern: createRegExp`
            \!|\d+|\+[\w\W]+|\-[\w\W]+|\*[\w\W]+|\/|\.|\\exp\\left\([\w\W]+\\right\)|\\exp\([\w\W]+\)|
            \\frac\d{2,}|\\frac\{[\w\W]+\}\{[\w\W]+\}|[a-zA-Z]+|\(|\)|\\left\([\w\W]+|\\right\([\w\W]+|\\sqrt[\w\W]+|
            \^|\\sin[\w\W]+|\\tan[\w\W]+|\\log[\w\W]+|\\cos[\w\W]+|\,|\\varphi(?:$|[^\w])|\\varsigma(?:$|[^\w])|
            \\epsilon(?:$|[^\w])|\\rho(?:$|[^\w])|\\tau(?:$|[^\w])|\\upsilon(?:$|[^\w])|\\theta(?:$|[^\w])|\\iota(?:$|[^\w])|
            \\omicron(?:$|[^\w])|\\pi(?:$|[^\w])|\\alpha(?:$|[^\w])|\\sigma(?:$|[^\w])|\\delta(?:$|[^\w])|\\phi(?:$|[^\w])|
            \\gamma(?:$|[^\w])|\\eta(?:$|[^\w])|\\xi(?:$|[^\w])|\\kappa(?:$|[^\w])|\\lambda(?:$|[^\w])|\\zeta(?:$|[^\w])|
            \\chi(?:$|[^\w])|\\psi(?:$|[^\w])|\\omega(?:$|[^\w])|\\beta(?:$|[^\w])|\\nu(?:$|[^\w])|\\mu(?:$|[^\w])|\\infty|
            \\varepsilon(?:$|[^\w])|\\vartheta(?:$|[^\w])|\\varkappa(?:$|[^\w])|\\varpi(?:$|[^\w])|\\Delta|\\Omega|
            \\varrho(?:$|[^\w])|\\times[\w\W]+
            ${'g'}
        `,

    hasRestrictedValue(invalidValueData, actualData) {
        return RESTRICTED_VALUES_ARRAY.includes(invalidValueData) || RESTRICTED_VALUES_ARRAY.some(symbol => actualData.includes(symbol));
    },

    getValidationMessage(value) {
        const invalidValue = reactMathKeyboardService.getInvalidValueFromInput(value);

        if (reactMathKeyboardService.hasRestrictedValue(invalidValue, value)) {
            const restrictedSymbol = RESTRICTED_VALUES_ARRAY.find(symbol => value.includes(symbol)) || invalidValue;
            return t('interactions.textEntry.reactMathKeyboard.symbolError', { invalidValue: restrictedSymbol });
        } else if (invalidValue) {
            return t('interactions.textEntry.reactMathKeyboard.notAllowedValues');
        }
        return '';
    },

    addMultiplicationSigns(expression) {
        let expressionValue = expression;
        // Add a multiplication sign between a number and a letter (e.g., 2x, 4sin)
        expressionValue = expressionValue.replace(/(\d)([a-zA-ZΑ-Ωα-ωϰϖϱ])/g, '$1*$2');
        // Add a multiplication sign between a closing parenthesis and a letter (e.g., 2*4, 12*tan)
        expressionValue = expressionValue.replace(/([)\d])([a-zA-ZΑ-Ωα-ωϰϖϱ])/g, '$1*$2');
        return expressionValue;
    },

    getMathInputStyles(size, isReadOnly) {
        let width = 'initial';
        let display = 'inline-block';
        if (isReadOnly) {
            width = 'fit-content';
            return { width, display };
        }

        if (size) {
            const widthMultiplier = MATH_INPUT_FIELD_WIDTH_MULTIPLIER;
            const iconWidth = ICON_WIDTH;

            const pixelWidth = Math.max(
                Math.min(
                    Math.floor(size * widthMultiplier),
                    MAX_CONTENT_WIDTH
                ),
                Math.floor(5 * widthMultiplier),
            );

            width = `${pixelWidth + iconWidth}px`;

            if (pixelWidth >= MAX_CONTENT_WIDTH) {
                width = 'initial';
                display = 'block';
            }
        }

        return { width, display };
    },

    validateOnQuestionReload(defaultValue, reactMathKeyboardValue, callback) {
        if (
            (typeof defaultValue === 'string' && typeof reactMathKeyboardValue === 'string')
            && (
                defaultValue === reactMathKeyboardValue
                || reactMathKeyboardValue.includes(defaultValue)
                || defaultValue.includes(reactMathKeyboardValue)
            )
        ) {
            const value = reactMathKeyboardValue.includes(defaultValue) ? reactMathKeyboardValue : defaultValue;
            callback(value);
        }
    },

    getValidationErrorMessage(value) {
        if (value.includes('placeholder')) {
            return t('interactions.textEntry.reactMathKeyboard.notAllowedValues');
        }

        return reactMathKeyboardService.getValidationMessage(value)
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>');
    },

    hasRestrictedSymbol(value) {
        const regex = /^[^<>=]*$/;
        return !regex.test(value);
    },

    getDataWithoutRestrictedSymbols(value) {
        return value.replace(/[<>=]/g, '');
    },

    getFocusedMathElement(questionModel, model, lastFocusedInputRef) {
        if (!questionModel || !model) return false;

        const id = `${questionModel.contentRevisionId}_${model.identifier}`;
        return lastFocusedInputRef?.current && lastFocusedInputRef.current?.getAttribute('id') === id;
    },

    handleOnInput(isCorrectMathElementFocused, value, mathRef, callback) {
        if (!isCorrectMathElementFocused) return;

        const valueWithMultiplication = value.replace(/\\times /g, '*');
        if (valueWithMultiplication === null || valueWithMultiplication === undefined) return;

        const dataWithoutRestrictedSymbols = reactMathKeyboardService.getDataWithoutRestrictedSymbols(valueWithMultiplication);
        if (reactMathKeyboardService.hasRestrictedSymbol(valueWithMultiplication)) {
            mathRef.current.latex(dataWithoutRestrictedSymbols);
        }

        callback(valueWithMultiplication, dataWithoutRestrictedSymbols);
    },

    handleSaveData(value) {
        const converter = new TeX2Max({ addTimesSign: marketplacePropertiesService.isSymbolicMathFromatImprovementEnabled() });

        let valueWithMultiplicationSigns = value;
        if (marketplacePropertiesService.isSymbolicMathFromatImprovementEnabled()) {
            valueWithMultiplicationSigns = reactMathKeyboardService.addMultiplicationSigns(value);
        }

        const convertExpLatex = (valueWithExp) => {
            const expPattern = /\\exp\\left\((.*?)\\right\)/g;
            return valueWithExp.replace(expPattern, 'exp($1)');
        };

        if (valueWithMultiplicationSigns.includes('\\exp')) {
            valueWithMultiplicationSigns = convertExpLatex(valueWithMultiplicationSigns);
        }

        const convertTimesToAsterix = valueWithMultiplicationSigns.replace(/\\times/g, '*').replace(/\s+/g, '');
        const maximaValue = convertTimesToAsterix ? converter.toMaxima(convertTimesToAsterix) : '';

        const updatedExpValue = maximaValue.replace(/exp\*\((\d+)\)/g, 'exp($1)');

        return updatedExpValue;
    },

    getMathFieldElement(questionModel, model, isFeedbackElement = false) {
        const id = questionModel?.contentRevisionId ?
            `${questionModel.contentRevisionId}_${model.identifier}` :
            model.target;
        const containerElement = document.getElementById(isFeedbackElement ? `${id}_feedback` : id);

        return containerElement?.querySelector('.react-math-keyboard-input');
    },

    handleKeyDown(event, mathRef, lastFocusedInputRef, a11yIIBScreenReaderV3Enabled) {
        const keyboardElement = document.querySelector('.react-math-keyboard-keyboard-container');

        const removeKeyboard = () => {
            document.body.style.paddingBottom = '0';
            keyboardElement.style.display = 'none';
            if (!a11yIIBScreenReaderV3Enabled) {
                mathRef.current.blur();
            }
        };
        if (event.key === 'Escape' && keyboardElement) removeKeyboard();

        if (lastFocusedInputRef.current && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();

            const isActiveElementKey = /\bmq-keyboard-\d+-button-key\b/.test(document.activeElement.id);
            const isActiveElementKeyClose = /\bmq-keyboard-\d+-button-key-close\b/.test(document.activeElement.id);
            const isActiveElementSelect = document.activeElement.classList.contains('react-math-keyboard-select');

            if (isActiveElementSelect) {
                const options = document.activeElement.querySelectorAll('option');
                const selectedIndex = Array.from(options).findIndex(option => option.selected);
                const nextIndex = (selectedIndex + 1) % options.length;

                options[nextIndex].selected = true;

                const changeEvent = new Event('change', { bubbles: true });
                document.activeElement.dispatchEvent(changeEvent);
            }

            if (isActiveElementKeyClose) {
                removeKeyboard();
            } else if (isActiveElementKey) {
                const mouseDownEvent = new MouseEvent('mousedown', { bubbles: true });
                document.activeElement.dispatchEvent(mouseDownEvent);
            }
        }
    },

    getElementForFocus(event, questionModel, model) {
        const findParentWithId = (element, id) => {
            let currentElement = element;
            while (currentElement && currentElement !== document.body) {
                if (currentElement.id === id) {
                    return currentElement;
                }
                currentElement = currentElement.parentElement;
            }
            return null;
        };

        const parentElementId = `${questionModel.contentRevisionId}_${model.identifier}`;
        return findParentWithId(event.target, parentElementId);
    },

    applyStylesByState(interactionProps, setInputStyles) {
        const {
            size,
            isReadOnly,
            handleInteractionReady,
            questionModel,
            model,
        } = interactionProps;
        const customInputStyles = reactMathKeyboardService.getMathInputStyles(size, isReadOnly);
        setInputStyles(customInputStyles);
        handleInteractionReady(true);

        const mathField = reactMathKeyboardService.getMathFieldElement(questionModel, model);
        if (mathField) {
            const bodyElement = document.querySelector('html > body');

            if (isReadOnly) {
                mathField.classList.add('readOnly');
                bodyElement.classList.add('removePaddingBottom');
            } else {
                mathField.classList.remove('readOnly');
                bodyElement.classList.remove('removePaddingBottom');
            }
        }

        const feedbackField = reactMathKeyboardService.getMathFieldElement(questionModel, model, true);
        if (feedbackField) {
            if (isReadOnly) {
                feedbackField.classList.add('readOnly');
            } else {
                feedbackField.classList.remove('readOnly');
            }
        }
    },

    handleDataOnPrevValueChange(computedProps, clearMathInputState) {
        const {
            valueFromPreviousSitting,
            reactMathKeyboardValue,
            validateReactMathKeyboard,
            mathRef,
        } = computedProps;

        if (
            valueFromPreviousSitting !== reactMathKeyboardValue && !valueFromPreviousSitting
        ) {
            clearMathInputState();
        }

        // Trigger validation if contrast, language or text size was changed
        // to show error message if it is exists
        if (valueFromPreviousSitting) {
            reactMathKeyboardService.validateOnQuestionReload(valueFromPreviousSitting, reactMathKeyboardValue, validateReactMathKeyboard);
            const latexOutput = convertAsciiMathToLatex(valueFromPreviousSitting);
            mathRef.current.latex(latexOutput);
        }
    },

    toggleErrorMessageClasses(questionModel, model, errorMessage) {
        const mathField = reactMathKeyboardService.getMathFieldElement(questionModel, model);
        if (mathField) {
            if (errorMessage) {
                mathField.classList.add('errorStyles');
            } else {
                mathField.classList.remove('errorStyles');
            }
        }
    },

    toggleIsFlaggedClasses(questionModel, model, isFlagged) {
        const mathField = reactMathKeyboardService.getMathFieldElement(questionModel, model);
        if (mathField) {
            if (isFlagged) {
                mathField.classList.add('flagged');
            } else {
                mathField.classList.remove('flagged');
            }
        }
    },

    handleInputStylesChange(questionModel, model, inputStyles) {
        const mathField = reactMathKeyboardService.getMathFieldElement(questionModel, model);
        if (mathField && inputStyles) {
            mathField.style.width = inputStyles.width;
            mathField.style.display = inputStyles.display;
        }
    },

    keyboardObserver: null,
    isObserverActive: false,

    getKeyNameToLabel(keyName) {
        const prefix = 'interactions.textEntry.reactMathKeyboard.labels.';
        const specialKeysMap = {
            'Delta': 'capitalDelta',
            'Omega': 'capitalOmega',
            'e^{\\square}': 'eToPower',
            'e^{square}': 'eToPower',
            'e^square': 'eToPower',
            'times': 'multiply',
            'frac': 'divide',
            'sqrt': 'squareRoot',
            'power': 'power',
            'sin_inverse': 'inverseSine',
            'cos_inverse': 'inverseCosine',
            'tan_inverse': 'inverseTangent',
            'arcsin': 'inverseSine',
            'arccos': 'inverseCosine',
            'arctan': 'inverseTangent',
            'asin': 'inverseSine',
            'acos': 'inverseCosine',
            'atan': 'inverseTangent',
            'leftParenthesis': 'leftParenthesis',
            'rightParenthesis': 'rightParenthesis',
            'left': 'moveLeft',
            'right': 'moveRight',
            'del': 'delete',
            'fx': 'fOfX',
        };
        const suffix = specialKeysMap[keyName] || keyName;

        return t(`${prefix}${suffix}`);
    },

    enhanceButton(button) {
        if (button.hasAttribute('data-a11y-enhanced')) return;
        if (button.hasAttribute('aria-label')) return;
        const idMatch = button.id.match(/button-key-(.*?)$/);
        if (!idMatch) return;
        const keyName = idMatch[1];

        // Skip enhancement for the close button to prevent NVDA interaction issues
        if (keyName === 'close') {
            button.setAttribute('aria-description', t('interactions.textEntry.reactMathKeyboard.labels.closeKeyboard'));
            return;
        }

        const label = reactMathKeyboardService.getKeyNameToLabel(keyName);
        if (label) {
            button.setAttribute('aria-label', label);
            const visualElements = button.querySelectorAll('span, svg');
            visualElements.forEach(el => el.setAttribute('aria-hidden', 'true'));
            button.setAttribute('data-a11y-enhanced', 'true');
        }
    },

    enhanceKeyboardAccessibility() {
        try {
            const existingButtons = document.querySelectorAll('.react-math-keyboard-key');
            existingButtons.forEach(button => reactMathKeyboardService.enhanceButton(button));

            if (reactMathKeyboardService.isObserverActive) return;
            reactMathKeyboardService.isObserverActive = true;

            reactMathKeyboardService.keyboardObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1) {
                            if (node.classList && node.classList.contains('react-math-keyboard-key')) {
                                reactMathKeyboardService.enhanceButton(node);
                            }
                            if (node.querySelectorAll) {
                                const buttons = node.querySelectorAll('.react-math-keyboard-key');
                                buttons.forEach(button => reactMathKeyboardService.enhanceButton(button));
                            }
                        }
                    });
                });
            });

            reactMathKeyboardService.keyboardObserver.observe(document.body, {
                childList: true,
                subtree: true,
            });
        } catch (error) {
            console.error('ReactMathKeyboardService: Failed to enhance keyboard accessibility', error);
        }
    },

    disconnectKeyboardObserver() {
        if (reactMathKeyboardService.keyboardObserver) {
            reactMathKeyboardService.keyboardObserver.disconnect();
            reactMathKeyboardService.keyboardObserver = null;
            reactMathKeyboardService.isObserverActive = false;
        }
    },
};

export default reactMathKeyboardService;
